/**
 * AshtamshaInfoTable Component
 * Displays D8 division information with planets that fall in each part
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  ASHTAMSHA_DIVISION_ATTRIBUTES,
  getLongitudeD8Part,
  parseLongitudeFromPosition,
  parseSunriseLongitude
} from '../data/AshtamshaTableData';
import { getAshtamshaGrahaRuler, getAshtamshaPlanetCode } from '../../../../../config/ashtamshaRulers';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay, PlanetDisplay } from '../helpers/planetDisplayHelper';
import { initializeDivisionGroups, DIVISION_COUNTS } from '../../../../../utils/divisionGrouping';
import {
  TABLE_WRAPPER_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TH_BASE_STYLES,
  TABLE_TD_BASE_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface AshtamshaInfoTableProps {
  apiResponse?: JatakaResponse | null;
  calculatedData?: {
    planetsByDivision: Record<number, PlanetDisplay[]>;
    totalPlanets: number;
    divisionsCount: number;
    calculationTime: number;
  };
}

/**
 * Group planets by D8 part they fall into (relative to sunrise longitude)
 * Now includes Lagna and retrograde indication
 */
function groupPlanetsByD8Part(apiResponse: JatakaResponse): Record<number, PlanetDisplay[]> {
  const planetsByPart = initializeDivisionGroups<PlanetDisplay>(DIVISION_COUNTS.ASHTAMSHA);

  // Get sunrise longitude for D8 calculation offset
  const sunriseLongitude = apiResponse.astronomical?.sunrise_surya_longitude
    ? parseSunriseLongitude(apiResponse.astronomical.sunrise_surya_longitude)
    : 0;

  // Get current hora planet for highlighting (same as Graha Chakra)
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Process each planet INCLUDING LAGNA
  if (apiResponse.graha_positions?.planets) {
    apiResponse.graha_positions.planets.forEach(planet => {
      // Parse longitude from position
      const longitude = parseLongitudeFromPosition(planet.position);

      // Determine which D8 part this planet falls into (relative to sunrise)
      const d8Part = getLongitudeD8Part(longitude, sunriseLongitude);

      // Use centralized planet display helper (DRY)
      const planetDisplay = createPlanetDisplay(planet, currentHoraPlanet);

      // Add planet to appropriate part
      planetsByPart[d8Part].push(planetDisplay);
    });
  }

  return planetsByPart;
}

export const AshtamshaInfoTable: React.FC<AshtamshaInfoTableProps> = ({ apiResponse, calculatedData }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Use calculated data from lazy loading if available, otherwise fallback to synchronous calculation
  const planetsByPart = calculatedData?.planetsByDivision || groupPlanetsByD8Part(apiResponse);

  // Get vara number for planetary rulers (API returns 0-based: 0=Sunday, 4=Thursday)
  const varaNumber = apiResponse.detailed_panchanga?.vara_details?.number ?? 0;

  return (
      <Box {...TABLE_WRAPPER_STYLES}>
      {/* Horizontal scrollable container */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.medium}>Prahara</Th>
              <Th {...COLUMN_STYLES.medium}>Graha</Th>
              <Th {...COLUMN_STYLES.xlarge}>Direction</Th>
              <Th {...COLUMN_STYLES.large}>Ruler</Th>
              <Th {...COLUMN_STYLES.xlarge}>Yoga</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Enemy (Ripu)</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Manifestations</Th>
            </Tr>
          </Thead>
          <Tbody>
            {ASHTAMSHA_DIVISION_ATTRIBUTES.map((division) => {
              const planetsInPart = planetsByPart[division.part] || [];

              return (
                <Tr key={division.part} _hover={TABLE_ROW_HOVER_STYLES}>
                  <Td {...TABLE_TD_BASE_STYLES} minW="20px">
                    {planetsInPart.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {planetsInPart.map((planet, index) => {
                          // Use planetType from helper (DRY)
                          const colors = getAshtamshaPlanetColor(planet.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${planet.shortName}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              px={1}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planet.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>
                  <Td {...TABLE_TD_BASE_STYLES} textAlign="center" fontWeight="normal">
                    {division.part}
                  </Td>
                  <Td {...TABLE_TD_BASE_STYLES} textAlign="center">
                    {getAshtamshaPlanetCode(getAshtamshaGrahaRuler(division.part, varaNumber))}
                  </Td>
                  <Td {...TABLE_TD_BASE_STYLES}>{division.direction}</Td>
                  <Td {...TABLE_TD_BASE_STYLES}>{division.ruler}</Td>
                  <Td {...TABLE_TD_BASE_STYLES}>{division.yoga}</Td>
                  <Td {...TABLE_TD_BASE_STYLES}>{division.enemy}</Td>
                  <Td {...TABLE_TD_BASE_STYLES}>{division.manifestations}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};